import React, { useState } from 'react';
import { BrowserRouter as Router, Routes, Route, Link, Navigate, useLocation } from 'react-router-dom';
import { AuthProvider, useAuth } from './contexts/AuthContext.tsx';
import { SubscriptionProvider, useSubscription } from './contexts/SubscriptionContext.tsx';
import ProtectedRoute from './components/ProtectedRoute.tsx';
import { BrandLogoMark, BrandWordmark } from './components/BrandLogo.tsx';
import Login from './components/Login.tsx';
import ForgotPassword from './components/ForgotPassword.tsx';
import EmployeeForm from './components/EmployeeForm.tsx';
import ShiftForm from './components/ShiftForm.tsx';
import HospitalForm from './components/HospitalForm.tsx';
import OperatingRoomScheduleForm from './components/OperatingRoomScheduleForm.tsx';
import BoardView from './components/BoardView.tsx';
import AnesthesiologistsView from './components/AnesthesiologistsView.tsx';
import CrnaView from './components/CrnaView.tsx';
import CredentialsView from './components/CredentialsView.tsx';
import ShiftsCard from './components/ShiftsCard.tsx';
import SimulateForm from './components/SimulateForm.tsx';
import ExternalMappingsManager from './components/ExternalMappingsManager.tsx';
import UsersView from './components/UsersView.tsx';
import ThemeToggle from './components/ThemeToggle.tsx';
import ErrorBoundary from './components/ErrorBoundary.tsx';
import RouteErrorBoundary from './components/RouteErrorBoundary.tsx';
import NotFound from './components/NotFound.tsx';
import { getLocalDateString } from './utils/timezone.ts';
import './App.css';

function Navigation() {
  const location = useLocation();
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const { user, isAdmin, logout } = useAuth();

  const navItems = [
    { path: '/simulate', label: 'Simulate', adminOnly: true },
    { path: '/board', label: 'Board' },
    { path: '/anesthesiologists', label: 'Anesthesiologists' },
    { path: '/crnas', label: 'CRNAs' },
    { path: '/credentials', label: 'Credentials' },
    { path: '/shifts', label: 'Manpower' },
    { path: '/schedules', label: 'Cases' },
    { path: '/employees', label: 'Employee Cards' },
    { path: '/hospitals', label: 'Hospitals & ORs' },
    { path: '/mappings', label: 'Mappings' },
    { path: '/users', label: 'Users', adminOnly: true }
    // Billing is intentionally absent: subscriptions are handled by ORchestrate, not by the
    // customer, so the page and its route are unlinked. BillingView.tsx is kept for when
    // self-serve billing is turned back on.
  ].filter(item => isAdmin || !item.adminOnly);

  const handleNavClick = () => {
    setIsMenuOpen(false);
  };

  const handleLogout = async () => {
    try {
      await logout();
    } catch (err) {
      console.error('Logout failed:', err);
    }
  };

  return (
    <nav className="nav">
      <div className="container nav-container">
        <button
          className="hamburger-menu"
          onClick={() => setIsMenuOpen(!isMenuOpen)}
          aria-label="Toggle menu"
        >
          <span></span>
          <span></span>
          <span></span>
        </button>
        <Link to="/" className="brand-mark" onClick={handleNavClick} aria-label="ORchestrate home">
          <BrandLogoMark size={26} />
          <BrandWordmark />
        </Link>
        <ul className={`nav-list ${isMenuOpen ? 'nav-list-open' : ''}`}>
          {navItems.map(item => (
            <li key={item.path} className="nav-item">
              <Link
                to={item.path}
                className={`nav-link ${location.pathname === item.path ? 'active' : ''}`}
                onClick={handleNavClick}
              >
                {item.label}
              </Link>
            </li>
          ))}
        </ul>
        <ThemeToggle />
        {user && !isAdmin && (
          <span
            title="You have read-only access"
            style={{
              fontSize: '11px',
              letterSpacing: '0.08em',
              textTransform: 'uppercase',
              color: 'var(--text-muted)',
              border: '1px solid var(--border-color, var(--text-muted))',
              borderRadius: '999px',
              padding: '2px 9px',
              whiteSpace: 'nowrap',
            }}
          >
            Read-only
          </span>
        )}
        {user && (
          <button
            onClick={handleLogout}
            className="logout-button"
          >
            Logout
          </button>
        )}
      </div>
    </nav>
  );
}

function AdminRoute({ children }: { children: React.ReactNode }) {
  const { isAdmin } = useAuth();
  if (!isAdmin) {
    return <Navigate to="/board" replace />;
  }
  return <>{children}</>;
}

function ShiftsPage() {
  const [searchDate, setSearchDate] = useState<string>(getLocalDateString());
  return (
    <div className="card">
      <h1>Manpower</h1>

      <div className="card" style={{ marginBottom: '16px' }}>
        <h3 style={{ marginTop: 0 }}>Create Shifts</h3>
        <ShiftForm />
      </div>

      <div className="card">
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          <h3 style={{ margin: 0 }}>Shifts</h3>
          <div style={{ display: 'flex', gap: '10px', alignItems: 'center' }}>
            <label htmlFor="shiftsDate" style={{ whiteSpace: 'nowrap' }}>Date:</label>
            <input
              id="shiftsDate"
              type="date"
              className="form-control"
              value={searchDate}
              onChange={(e) => setSearchDate(e.target.value)}
            />
          </div>
        </div>
        <div style={{ marginTop: '10px' }}>
          <ShiftsCard selectedDate={searchDate} hasPersistedAllocations={false} showDeleteAll={true} />
        </div>
      </div>
    </div>
  );
}

/**
 * App-wide paywall notice. Renders only when the backend reports this tenant as write-blocked,
 * so it stays invisible with enforcement off, for grandfathered tenants, and whenever the
 * status could not be read (the context fails open — see SubscriptionContext).
 */
function SubscriptionNotice() {
  const { canWrite } = useSubscription();
  if (canWrite) return null;

  return (
    <div
      role="status"
      style={{
        display: 'flex',
        alignItems: 'center',
        gap: '10px',
        flexWrap: 'wrap',
        padding: '10px 14px',
        margin: '12px 0',
        borderRadius: '6px',
        fontSize: '13px',
        border: '1px solid var(--danger-text, #dc2626)',
        background: 'rgba(220, 38, 38, 0.08)',
        color: 'var(--text-primary)',
      }}
    >
      <span aria-hidden="true">🔒</span>
      <span>
        <strong>Read-only.</strong> Your ORchestrate subscription is not active, so schedules and
        allocations can be viewed but not changed. Contact ORchestrate support to restore access.
      </span>
    </div>
  );
}

function App() {
  return (
    // Outermost boundary: catches anything the routed boundary cannot, including
    // a throw from the Router or AuthProvider themselves. It hides the "Back to
    // board" link because routing may be exactly what is broken.
    <ErrorBoundary hideBoardLink>
      <Router>
        <AuthProvider>
          <div className="App">
            <Routes>
              <Route path="/login" element={<Login />} />
              <Route path="/forgot-password" element={<ForgotPassword />} />
              <Route
                path="/*"
                element={
                  <ProtectedRoute>
                    <SubscriptionProvider>
                      <Navigation />
                      <div className="container">
                        {/* Paywall notice: app-wide, so it is visible from whichever screen
                            the user is on when a write starts failing. */}
                        <SubscriptionNotice />
                        {/* Inside the nav, so a screen crash leaves the menu usable. */}
                        <RouteErrorBoundary>
                          <Routes>
                            <Route path="/" element={<Home />} />
                            <Route path="/board" element={<BoardView />} />
                            <Route path="/anesthesiologists" element={<AnesthesiologistsView />} />
                            <Route path="/crnas" element={<CrnaView />} />
                            <Route path="/credentials" element={<CredentialsView />} />
                            <Route path="/employees" element={<EmployeeForm />} />
                            <Route path="/shifts" element={<ShiftsPage />} />
                            <Route path="/hospitals" element={<HospitalForm />} />
                            <Route path="/schedules" element={<OperatingRoomScheduleForm />} />
                            <Route path="/simulate" element={<AdminRoute><SimulateForm /></AdminRoute>} />
                            <Route path="/mappings" element={<ExternalMappingsManager />} />
                            <Route path="/users" element={<AdminRoute><UsersView /></AdminRoute>} />
                            <Route path="*" element={<NotFound />} />
                          </Routes>
                        </RouteErrorBoundary>
                      </div>
                    </SubscriptionProvider>
                  </ProtectedRoute>
                }
              />
            </Routes>
          </div>
        </AuthProvider>
      </Router>
    </ErrorBoundary>
  );
}

function Home() {
  const { isAdmin } = useAuth();
  const items: { path: string; title: string; desc: string; adminOnly?: boolean }[] = [
    { path: '/board', title: 'Board', desc: 'View daily OR schedules and allocations across every hospital, in one timeline.' },
    { path: '/shifts', title: 'Manpower', desc: 'Define when CRNAs, students, and physicians are available — and where.' },
    { path: '/schedules', title: 'Cases', desc: 'Create and edit operating-room cases for the day.' },
    { path: '/simulate', title: 'Simulate', desc: 'Generate test shifts and schedules to validate the allocation pipeline.' },
    { path: '/employees', title: 'Employee Cards', desc: 'Manage providers, credentials, and home hospitals.' },
    { path: '/hospitals', title: 'Hospitals & ORs', desc: 'Add facilities and manage their operating rooms (Endo, C/S, MRI…).' },
    { path: '/mappings', title: 'Mappings', desc: 'Configure external system identifiers for integration.' },
  ].filter(item => isAdmin || !item.adminOnly);
  return (
    <div style={{ position: 'relative', padding: '32px 4px 16px' }}>
      <p style={{ fontSize: '11px', letterSpacing: '0.22em', textTransform: 'uppercase', color: 'var(--amber)', marginBottom: '14px' }}>
        OR Allocation · Console
      </p>
      <h1 style={{ fontFamily: 'var(--font-serif)', fontSize: '54px', lineHeight: 1.08, marginBottom: '16px', maxWidth: '780px' }}>
        The <em style={{ fontStyle: 'italic', color: 'var(--or-blue-mid)' }}>OR</em> finally<br />runs itself.
      </h1>
      <p style={{ fontSize: '16px', color: 'var(--text-muted)', maxWidth: '620px', lineHeight: 1.65, marginBottom: '40px', fontWeight: 300 }}>
        Intelligent, one-click scheduling for anesthesia groups. Pick a workspace below to begin.
      </p>
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(280px, 1fr))', gap: '18px' }}>
        {items.map(item => (
          <Link
            key={item.path}
            to={item.path}
            style={{ textDecoration: 'none', color: 'inherit' }}
          >
            <div
              className="card"
              style={{
                margin: 0,
                padding: '22px 22px 24px',
                borderTop: '2px solid rgba(217, 119, 6, 0.4)',
                height: '100%',
                transition: 'border-color 0.15s ease, transform 0.15s ease, box-shadow 0.15s ease',
                cursor: 'pointer',
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.borderTopColor = 'var(--amber)';
                e.currentTarget.style.transform = 'translateY(-2px)';
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.borderTopColor = 'rgba(217, 119, 6, 0.4)';
                e.currentTarget.style.transform = 'translateY(0)';
              }}
            >
              <div style={{ fontSize: '15px', fontWeight: 600, color: 'var(--text-primary)', marginBottom: '9px' }}>{item.title}</div>
              <div style={{ fontSize: '13px', color: 'var(--text-muted)', lineHeight: 1.6 }}>{item.desc}</div>
            </div>
          </Link>
        ))}
      </div>
    </div>
  );
}

export default App;
